// components/TimelineBar.jsx
import React, { useEffect, useRef, useState, useMemo } from "react";
import { motion, useMotionValue } from "framer-motion";

export default function TimelineBar({
  duration,
  current,
  onSeek,
  onSeekEnd,
  zoom = 1, // new prop: 1 = 100%
  pxPerSec,
  keyframeMarkers = [],
  activeSlideIndex,
  onKeyframeClick
}) {
  const containerRef = useRef(null); // visible viewport
  const rulerRef = useRef(null); // the wide inner ruler
  const x = useMotionValue(0);
  const [isDragging, setIsDragging] = useState(false);
  const [containerWidth, setContainerWidth] = useState(800); // fallback

  // Observe container size so we can compute inner width
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    function update() {
      setContainerWidth(Math.max(1, Math.floor(el.clientWidth)));
    }
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

// universal timeline width
  const innerWidth = duration * pxPerSec * zoom;

  // Choose tick spacing so labels are roughly ~50 px apart (adjustable)
  const markers = useMemo(() => {
    if (!duration || duration <= 0) return [];
    const pixelsPerSecond = innerWidth / duration;
    // target 50px between labeled ticks
    const targetPx = 50;
    const stepSeconds = Math.max(1, Math.ceil(targetPx / pixelsPerSecond));
    const arr = [];
    for (let t = 0; t <= Math.ceil(duration); t += stepSeconds) {
      arr.push(t);
    }
    // ensure final time is present
    if (arr[arr.length - 1] !== Math.ceil(duration)) arr.push(Math.ceil(duration));
    return arr;
  }, [duration, innerWidth]);

  // Update playhead when not dragging (in pixels relative to innerWidth)
  useEffect(() => {
    if (!duration || isDragging) return;
    const px = (current / duration) * innerWidth;
    x.set(px);
  }, [current, duration, innerWidth, isDragging, x]);

  const getTimeFromClientX = (clientX) => {
    const rect = rulerRef.current?.getBoundingClientRect();
    if (!rect || !duration) return 0;
    const rel = Math.max(0, Math.min(clientX - rect.left, rect.width));
    return (rel / rect.width) * duration;
  };

  const handleClick = (e) => {
    const t = getTimeFromClientX(e.clientX);
    onSeek?.(t);
    onSeekEnd?.(t);
  };

  const handleDragStart = () => {
    setIsDragging(true);
  };

  const handleDrag = (_, info) => {
    // use clientX so scrolling is considered
    const t = getTimeFromClientX(info.point.x);
    onSeek?.(t); // preview seek
    // set playhead visually while dragging
    const px = (t / duration) * innerWidth;
    x.set(px);
  };

  const handleDragEnd = (_, info) => {
    const t = getTimeFromClientX(info.point.x);
    setIsDragging(false);
    onSeekEnd?.(t); // commit
    const px = (t / duration) * innerWidth;
    x.set(px);
  };

  // helper to format time as mm:ss when large durations
  const formatLabel = (s) => {
    if (s >= 60) {
      const m = Math.floor(s / 60);
      const sec = Math.floor(s % 60).toString().padStart(2, "0");
      return `${m}:${sec}`;
    }
    return `${Math.floor(s)}s`;
  };

  return (
  <div
    className="
      w-full rounded-md p-2
      bg-gray-200 dark:bg-gray-900
      border border-gray-200 dark:border-gray-800
      text-gray-900 dark:text-gray-300
      transition-colors
    "
  >
    {/* viewport */}
    <div
      ref={containerRef}
      className="
        relative w-full h-16
        cursor-pointer select-none
        overflow-x-auto
      "
      onClick={handleClick}
    >
      {/* ruler (variable width) */}
      <div
        ref={rulerRef}
        className="relative h-full"
        style={{ width: `${innerWidth}px`, minWidth: "100%" }}
      >
        {/* ───────────── Time ruler ticks ───────────── */}
        <div className="absolute top-0 left-0 h-full w-full pointer-events-none">
          {markers.map((t, i) => {
            const posPx = (t / duration) * innerWidth;
            return (
              <div
                key={i}
                className="absolute flex flex-col items-center"
                style={{
                  left: posPx,
                  transform: "translateX(-50%)",
                }}
              >
                {/* Tick */}
                <div className="w-px h-3 bg-gray-600 dark:bg-gray-400" />

                {/* Label */}
                <div className="mt-1 text-[10px] text-gray-600 dark:text-gray-400">
                  {formatLabel(t)}
                </div>
              </div>
            );
          })}
        </div>

        {/* ───────────── Keyframe markers ───────────── */}
        {keyframeMarkers.map((kf, i) => {
          const posPx = (kf.time / duration) * innerWidth;
          const isActive = kf.slideIndex === activeSlideIndex;

          return (
            <div
              key={i}
              className="absolute top-1 z-30 cursor-pointer"
              style={{
                left: posPx,
                transform: "translateX(-50%)",
              }}
              onClick={() => onSeek?.(kf.time)}
              title={`Keyframe @ ${kf.time.toFixed(2)}s`}
            >
              <div
                className={`
                  w-2 h-2 rotate-45
                  ${isActive
                    ? "bg-yellow-400"
                    : "bg-gray-400 dark:bg-gray-500"}
                `}
              />
            </div>
          );
        })}

        {/* ───────────── Playhead ───────────── */}
        <motion.div
          className="absolute top-0 bottom-0 w-[2px] bg-blue-500 z-20"
          style={{ x }}
          drag="x"
          dragConstraints={containerRef}
          dragElastic={0}
          onDragStart={handleDragStart}
          onDrag={handleDrag}
          onDragEnd={handleDragEnd}
        >
          <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-3 h-3 bg-blue-500 rounded-full" />
        </motion.div>

        {/* ───────────── Clip bar ───────────── */}
        <div
          className="
            absolute bottom-0 left-0 h-5 w-full
            rounded-sm flex items-center justify-center
            text-[11px] font-medium
            bg-purple-500/30 dark:bg-purple-500/40
            text-purple-900 dark:text-white
          "
        >
          Current Clip
        </div>
      </div>
    </div>
  </div>
);

}
